import React from 'react';

import { Select } from '@aline/common';
import { IconButton } from '@mui/material';
import { Check, X } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  accept: {
    color: theme.appColors.acceptSuggestion,
  },
  // No gap: the buttons' own padding is all the space between the two glyphs.
  group: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
  },
  reject: {
    color: theme.appColors.rejectSuggestion,
  },
}));

interface SuggestionActionsProps {
  onAccept: () => void;
  onReject: () => void;
  /** Dims ✓ and explains why; the server refuses the same accept. */
  acceptDisabledReason?: string | null;
  acceptTitle?: string;
  disabled?: boolean;
  rejectTitle?: string;
  size?: number;
}

/** The ✕ / ✓ pair every suggestion carries. Live whether or not it is the current stop. */
export const SuggestionActions = React.memo(function SuggestionActions({
  acceptDisabledReason = null,
  acceptTitle = 'Accept',
  disabled = false,
  onAccept,
  onReject,
  rejectTitle = 'Reject',
  size = 16,
}: SuggestionActionsProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <span
      className={classes.group}
      // A press here decides a suggestion; it must not select the row or start a drag.
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      <AlineTooltip arrow placement="top" title={rejectTitle}>
        <IconButton
          className={classes.reject}
          data-cy={Select.playbooks.suggestionReject}
          disabled={disabled}
          size="small"
          onClick={onReject}
        >
          <X size={size} />
        </IconButton>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title={acceptDisabledReason ?? acceptTitle}>
        {/* Wrapped so the tooltip still works while the button is disabled. */}
        <span>
          <IconButton
            className={classes.accept}
            data-cy={Select.playbooks.suggestionAccept}
            disabled={disabled || Boolean(acceptDisabledReason)}
            size="small"
            onClick={onAccept}
          >
            <Check size={size} />
          </IconButton>
        </span>
      </AlineTooltip>
    </span>
  );
});

function stopPropagation(event: React.SyntheticEvent): void {
  event.stopPropagation();
}
